import { useMemo } from 'react';
import { useGetNextAppointmentQuery } from '@features/appointments';
import { format } from 'date-fns';

/**
 * Hook for fetching and formatting upcoming appointments
 * Handles data transformation for display in the dashboard
 */
export const useUpcomingAppointments = () => {
  // Fetch next 20 appointments using RTK Query
  const {
    data: appointmentsData,
    isLoading,
    error,
  } = useGetNextAppointmentQuery({
    num: 20,
  });

  // Format appointments for display
  const formattedAppointments = useMemo(() => {
    if (!appointmentsData?.items) return [];

    return appointmentsData.items.map((appointment, index) => {
      // Format time as "11:00 am"
      const displayTime = appointment.start_time
        ? format(new Date(appointment.start_time), 'h:mm a').toLowerCase()
        : '';

      // Get patient name with initial for last name
      const patientName = appointment.patient?.name || 'Unknown Patient';
      const nameParts = patientName.split(' ');
      const displayName =
        nameParts.length > 1
          ? `${nameParts[0]} ${nameParts[nameParts.length - 1].charAt(0)}`
          : nameParts[0];

      // Calculate duration (for now, we'll use a default or parse from encounter_type)
      // In real implementation, this would come from appointment.duration or calculated from start/end
      const duration = String(appointment.duration || '30min');

      // Get appointment type
      const appointmentType = appointment.encounter_type || 'Check-in';

      return {
        id: appointment.appointment_id,
        time: displayTime,
        patientName: displayName,
        fullPatientName: patientName,
        duration,
        appointmentType,
        isNext: index === 0, // First appointment is "Next"
        encounter: appointment.encounter as unknown,
        originalAppointment: appointment, // Keep original for navigation
      };
    });
  }, [appointmentsData]);

  return {
    appointments: formattedAppointments,
    loading: isLoading,
    error,
  };
};
